import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import BackButton from '../../../components/common/BackButton';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import Pagination from '../../../components/common/Pagination';
import { useAuth } from '../../../contexts/AuthContext';
import { openProtectedFile } from '../../../utils/openProtectedFile';
import {
  getExpenses,
  createExpense,
  deleteExpense as deleteExpenseApi,
} from '../../../services/expensesService';
import { getApiErrorMessage } from '../../../services/http';

const ExpenseForm = () => {
  const { user } = useAuth();
  const role = user?.role;
  // Form fields
  const [date, setDate] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('General');
  const [notes, setNotes] = useState('');
  const [receipt, setReceipt] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // List and filters
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize] = useState(10);

  const exportToCSV = (filename, rows, columns) => {
    try {
      const header = columns.map(c => '"' + c.header + '"').join(',');
      const body = rows.map(r => columns.map(c => '"' + (c.accessor(r) ?? '') + '"').join(',')).join('\n');
      const csv = header + '\n' + body;
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error('CSV export failed', e);
      toast.error('Export failed');
    }
  };

  useEffect(() => {
    // Defaults
    const today = new Date();
    const fmt = (d) => d.toISOString().slice(0, 10);
    setDate(fmt(today));
    const start = new Date();
    start.setDate(today.getDate() - 6);
    setStartDate(fmt(start));
    setEndDate(fmt(today));
  }, []);

  const fetchList = async () => {
    try {
      setLoading(true);
      const data = await getExpenses({
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        category: categoryFilter || undefined,
        search: search || undefined,
        page: currentPage,
        limit: pageSize,
      });
      setList(data?.expenses || []);
      setTotal(data?.total || 0);
      setTotalPages(data?.totalPages || 1);
    } catch (e) {
      console.error('Load expenses failed', e);
      toast.error(getApiErrorMessage(e, 'Failed to load expenses'));
    } finally {
      setLoading(false);
    }
  };

  const askDelete = (exp) => {
    setExpenseToDelete(exp);
    setConfirmOpen(true);
  };

  const deleteExpense = async () => {
    if (!expenseToDelete) return;
    try {
      await deleteExpenseApi(expenseToDelete._id);
      toast.success('Expense deleted');
      setConfirmOpen(false);
      setExpenseToDelete(null);
      fetchList();
    } catch (e) {
      setConfirmOpen(false);
      setExpenseToDelete(null);
      toast.error(getApiErrorMessage(e, 'Failed to delete expense'));
    }
  };

  useEffect(() => {
    if (startDate && endDate) {
      setCurrentPage(1); // Reset to first page when filters change
      fetchList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, categoryFilter, search]);

  // Refetch when pagination changes
  useEffect(() => {
    if (startDate && endDate) fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const onFileChange = (e) => {
    setReceipt(e.target.files?.[0] || null);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!date || !description || !amount || !category) {
      toast.error('Fill all required fields');
      return;
    }
    try {
      setSubmitting(true);
      const form = new FormData();
      form.append('date', date);
      form.append('description', description.trim());
      form.append('amount', String(amount));
      form.append('category', category);
      if (notes) form.append('notes', notes.trim());
      if (receipt) form.append('receipt', receipt);
      await createExpense(form);
      toast.success('Expense saved');
      setDescription('');
      setAmount('');
      setCategory('General');
      setNotes('');
      setReceipt(null);
      fetchList();
    } catch (e) {
      console.error('Create expense failed', e);
      toast.error(getApiErrorMessage(e, 'Failed to save expense'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <BackButton to="/dashboard" />
        <h2 className="text-2xl font-bold text-gray-900">Expenses</h2>
      </div>

      {/* Create Expense */}
      <form onSubmit={onSubmit} className="relative overflow-hidden rounded-2xl shadow-lg border border-red-200 bg-gradient-to-br from-red-50 via-rose-50 to-pink-50">
        <div className="p-6 space-y-4">
          <h3 className="text-lg font-bold text-red-900">New Expense</h3>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <input type="date" className="input-field" value={date} onChange={e => setDate(e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select className="input-field" value={category} onChange={e => setCategory(e.target.value)}>
              <option>General</option>
              <option>Rent</option>
              <option>Utilities</option>
              <option>Supplies</option>
              <option>Transport</option>
              <option>Maintenance</option>
              <option>Other</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
            <input type="number" step="0.01" className="input-field" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" />
          </div>
          <div className="md:col-span-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input className="input-field" value={description} onChange={e => setDescription(e.target.value)} placeholder="Describe the expense" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Receipt (optional)</label>
            <input type="file" onChange={onFileChange} className="block w-full text-sm text-gray-600" />
          </div>
          <div className="md:col-span-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea className="input-field" rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
          </div>
        </div>
        <div>
          <button type="submit" className="btn-primary" disabled={submitting}>{submitting ? 'Saving...' : 'Save Expense'}</button>
        </div>
        </div>
      </form>

      {/* Filters */}
      <div className="relative overflow-hidden rounded-2xl shadow-lg border border-blue-200 bg-gradient-to-br from-blue-50 via-indigo-50 to-cyan-50">
        <div className="p-6 space-y-4">
          <h3 className="text-lg font-bold text-blue-900">Filter Expenses</h3>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input type="date" className="input-field" value={startDate} onChange={e => setStartDate(e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input type="date" className="input-field" value={endDate} onChange={e => setEndDate(e.target.value)} />
          </div>
          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select className="input-field" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
              <option value="">All</option>
              <option>General</option>
              <option>Rent</option>
              <option>Utilities</option>
              <option>Supplies</option>
              <option>Transport</option>
              <option>Maintenance</option>
              <option>Other</option>
            </select>
          </div>
          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input className="input-field" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search description" />
          </div>
        </div>
        </div>
      </div>

      {/* Expense List */}
      <div className="relative overflow-hidden rounded-2xl shadow-lg border border-orange-200 bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-orange-900">All Expenses</h3>
              <p className="text-sm text-orange-600">Expense history and records</p>
            </div>
          <button
            className="btn-secondary"
            onClick={() => exportToCSV(
              `expenses-${startDate}-to-${endDate}.csv`,
              list,
              [
                { header: 'Date', accessor: r => r.date ? new Date(r.date).toLocaleDateString() : '' },
                { header: 'Category', accessor: r => r.category },
                { header: 'Description', accessor: r => r.description },
                { header: 'Amount', accessor: r => r.amount },
                { header: 'Created By', accessor: r => r.createdBy?.name },
              ]
            )}
          >Export CSV</button>
          </div>
          <div className="overflow-x-auto bg-white rounded-xl shadow-inner">
            <table className="min-w-full divide-y divide-orange-100">
              <thead className="bg-gradient-to-r from-orange-50 to-amber-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold text-orange-800 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-orange-800 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-orange-800 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-right text-xs font-bold text-orange-800 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-orange-800 uppercase tracking-wider">Receipt</th>
                {role === 'admin' && (
                  <th className="px-6 py-3 text-right text-xs font-bold text-orange-800 uppercase tracking-wider">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {list.map((exp, index) => (
                <tr key={exp._id} className={`hover:bg-orange-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{exp.date ? new Date(exp.date).toLocaleDateString() : ''}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{exp.category}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{exp.description}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">KSh {Number(exp.amount || 0).toFixed(2)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {exp.receipt?.filename ? (
                      <button
                        type="button"
                        onClick={() => openProtectedFile(exp.receipt.filename)}
                        className="text-primary-600 hover:text-primary-800 underline"
                      >
                        View
                      </button>
                    ) : (
                      '-'
                    )}
                  </td>
                  {role === 'admin' && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                      <button className="text-red-600 hover:text-red-800" onClick={() => askDelete(exp)}>Delete</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            </table>
          </div>
          {loading && <div className="text-center text-gray-500 py-6">Loading...</div>}
          {!loading && list.length === 0 && <div className="text-center text-gray-500 py-6">No expenses</div>}

          {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          total={total}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
        />
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={deleteExpense}
        title="Delete Expense"
        message={`Are you sure you want to delete this expense${expenseToDelete ? ` (${expenseToDelete.description})` : ''}?`}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

export default ExpenseForm; 